import React, { useEffect, useState } from "react";
import { X, Check, Shield, Calendar, MapPin, Pen, Ban, Send, Star, Award, KeyRound, Copy, Mail } from "lucide-react";
import { useAdmin } from "./AdminContext";
import { fetchAdminUserProfile, setAdminNote, adminSetSuspended, adminSendDm, adminSetBamCoach, adminSetCertPurchased, adminSetCertFounding, adminSetMetricExclusion } from "../utils/communityApi";
import { GOLD } from "../constants/theme";
import { InternAdminSection } from "../interns/InternAdminSection";
import { supabase } from "../../supabaseClient";
import { bamConfirm, bamToast } from "./dialog";

// Slide-out panel that shows admin-only info about a user — signup date,
// subscription status, admin notes, etc. Opens when any code calls
// useAdmin().setOpenUser({ id, name?, avatarUrl? }).
export function UserAdminPanel({ C, dark }) {
  const { isAdmin, adminMode, openUser, setOpenUser } = useAdmin();
  const [profile, setProfile] = useState(null);
  const [testSaving, setTestSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [suspending, setSuspending] = useState(false);
  const [bamCoachSaving, setBamCoachSaving] = useState(false);
  const [certSaving, setCertSaving] = useState(false);
  const [dmOpen, setDmOpen] = useState(false);
  const [dmSubject, setDmSubject] = useState("");
  const [dmBody, setDmBody] = useState("");
  const [dmSending, setDmSending] = useState(false);
  const [dmSent, setDmSent] = useState(false);
  const [foundingSaving, setFoundingSaving] = useState(false);
  const [resetLink, setResetLink] = useState(null);
  const [resetBusy, setResetBusy] = useState(false);
  const [resetErr, setResetErr] = useState(null);
  const [resetCopied, setResetCopied] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);
  const [emailEdit, setEmailEdit] = useState(null);     // null = not editing
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailErr, setEmailErr] = useState(null);

  const open = !!(isAdmin && adminMode && openUser);

  useEffect(() => {
    let cancelled = false;
    if (!open) { setProfile(null); setNote(""); return; }
    setLoading(true);
    fetchAdminUserProfile(openUser.id).then(p => {
      if (cancelled) return;
      setProfile(p);
      setNote(p?.admin_notes || "");
      setLoading(false);
    }).catch(e => {
      if (cancelled) return;
      console.error("[UserAdminPanel] profile:", e);
      setProfile(null);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [open, openUser?.id]);

  if (!open) return null;

  const close = () => setOpenUser(null);
  const save = async () => {
    if (!profile) return;
    setSaving(true);
    try {
      await setAdminNote(profile.id, note);
      setSavedAt(Date.now());
    } catch (e) {
      console.error("setAdminNote:", e);
 bamToast("Could not save note: are you still an admin?", { tone: "error" });
    } finally {
      setSaving(false);
    }
  };

  // A reset LINK rather than "resend the reset email", because the email is
  // the thing that is usually broken: auth mail goes through Supabase's own
  // SMTP, which is a different pipe from everything else this app sends, and
  // a resend through a broken pipe reports success and delivers nothing.
  // This hands back a link the admin can paste into whatever thread they are
  // already in with the coach.
  const makeResetLink = async () => {
    if (!profile || resetBusy) return;
    setResetBusy(true); setResetErr(null); setResetLink(null); setResetCopied(false);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch("/api/admin-actions?action=admin-reset-link", {
        method: "POST",
        headers: {
          "content-type": "application/json",
          ...(session ? { authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({ email: profile.email, origin: window.location.origin }),
      });
      const out = await r.json().catch(() => ({}));
      if (!r.ok || !out.link) throw new Error(out.error || "Could not generate a link");
      setResetLink(out.link);
    } catch (e) {
      setResetErr(e.message || "Could not generate a link");
    } finally {
      setResetBusy(false);
    }
  };

  const copyResetLink = async () => {
    try {
      await navigator.clipboard.writeText(resetLink);
      setResetCopied(true);
      setTimeout(() => setResetCopied(false), 1800);
    } catch {
      setResetErr("Copy failed. Select the link and copy it by hand.");
    }
  };

  // The panel showed name, handle, role, location and bio, and never the one
  // field support actually needs. A coach who cannot sign in is usually
  // typing a different address from the one on the account, and without this
  // there was no way to tell them which one is theirs.
  const copyEmail = async () => {
    if (!profile?.email) return;
    try {
      await navigator.clipboard.writeText(profile.email);
      setEmailCopied(true);
      setTimeout(() => setEmailCopied(false), 1600);
    } catch { /* it is selectable text, so they can copy it by hand */ }
  };

  // Changing the sign-in address. The reason this is worth a control rather
  // than a trip to the Supabase dashboard: a coach who signed up with a typo
  // gets reset links that work perfectly on an account they are not typing
  // into, and that is indistinguishable from a broken link from their side.
  const saveEmail = async () => {
    const next = String(emailEdit || "").trim().toLowerCase();
    if (!profile || emailSaving || !next) return;
    if (next === String(profile.email || "").toLowerCase()) { setEmailEdit(null); return; }
    if (!(await bamConfirm({ title: `Change this coach's sign-in email to ${next}?`, message: "They will have to sign in with the new address from now on.", confirmLabel: "Change" }))) return;
    setEmailSaving(true); setEmailErr(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch("/api/admin-actions?action=admin-set-email", {
        method: "POST",
        headers: {
          "content-type": "application/json",
          ...(session ? { authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({ userId: profile.id, email: next }),
      });
      const out = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(out.error || "Could not change the email");
      setProfile({ ...profile, email: out.email || next });
      setEmailEdit(null);
    } catch (e) {
      setEmailErr(e.message || "Could not change the email");
    } finally {
      setEmailSaving(false);
    }
  };

  const sendDm = async () => {
    if (!profile || dmSending) return;
    if (!dmSubject.trim() || !dmBody.trim()) return;
    setDmSending(true);
    try {
      await adminSendDm({ userId: profile.id, userEmail: profile.email, subject: dmSubject, body: dmBody });
      setDmSent(true);
      setDmSubject(""); setDmBody("");
      setTimeout(() => { setDmSent(false); setDmOpen(false); }, 1600);
    } catch (e) {
      console.error("adminSendDm:", e);
      bamToast(e.message || "Send failed", { tone: "error" });
    } finally {
      setDmSending(false);
    }
  };

  // Toggle the BAM Coach staff flag. Optimistic local update; reverts if
  // the RPC fails. The RPC writes an audit_log row server-side.
  const toggleBamCoach = async () => {
    if (!profile || bamCoachSaving) return;
    const next = !profile.is_bam_coach;
    setBamCoachSaving(true);
    setProfile({ ...profile, is_bam_coach: next }); // optimistic
    try {
      await adminSetBamCoach(profile.id, next);
    } catch (e) {
      console.error("adminSetBamCoach:", e);
      bamToast(e.message || "Failed to update BAM Coach flag", { tone: "error" });
      setProfile({ ...profile, is_bam_coach: !next }); // revert
    } finally {
      setBamCoachSaving(false);
    }
  };

  // Founding cert — pre-platform buyers, platform free for life. Optimistic.
  const toggleFounding = async () => {
    if (!profile || foundingSaving) return;
    const next = !profile.cert_founding;
    setFoundingSaving(true);
    setProfile({ ...profile, cert_founding: next }); // optimistic
    try {
      await adminSetCertFounding(profile.id, next);
    } catch (e) {
      console.error("adminSetCertFounding:", e);
      bamToast(e.message || "Failed to update founding cert (has the 2026-07-30 migration run?)", { tone: "error" });
      setProfile({ ...profile, cert_founding: !next }); // revert
    } finally {
      setFoundingSaving(false);
    }
  };

  // Test / internal account: out of every admin count. Optimistic.
  const toggleTest = async () => {
    if (!profile || testSaving) return;
    const next = !profile.exclude_from_metrics;
    setTestSaving(true);
    setProfile({ ...profile, exclude_from_metrics: next, exclude_reason: next ? "Marked in admin" : null });
    try {
      await adminSetMetricExclusion(profile.id, next, next ? "Marked in admin" : null);
    } catch (e) {
      console.error("adminSetMetricExclusion:", e);
      bamToast(e.message || "Couldn't update (has the 2026-10-15 metrics SQL been run?)", { tone: "error" });
      setProfile({ ...profile });
    } finally {
      setTestSaving(false);
    }
  };

  // Grant / revoke certification access. Optimistic; reverts on failure.
  // The RPC re-checks admin + writes an audit_log row.
  const toggleCert = async () => {
    if (!profile || certSaving) return;
    const next = !profile.cert_purchased;
    setCertSaving(true);
    setProfile({ ...profile, cert_purchased: next }); // optimistic
    try {
      await adminSetCertPurchased(profile.id, next);
    } catch (e) {
      console.error("adminSetCertPurchased:", e);
      bamToast(e.message || "Failed to update certification access", { tone: "error" });
      setProfile({ ...profile, cert_purchased: !next }); // revert
    } finally {
      setCertSaving(false);
    }
  };

  const toggleSuspend = async () => {
    if (!profile || suspending) return;
    const next = !profile.is_suspended;
    const verb = next ? "suspend" : "reactivate";
    if (!(await bamConfirm({ title: `Are you sure you want to ${verb} ${profile.first_name || profile.email}?`, confirmLabel: next ? "Suspend" : "Reactivate", danger: next }))) return;
    setSuspending(true);
    try {
      await adminSetSuspended(profile.id, next);
      setProfile({ ...profile, is_suspended: next });
    } catch (e) {
      console.error("adminSetSuspended:", e);
      bamToast(e.message || "Failed", { tone: "error" });
    } finally {
      setSuspending(false);
    }
  };

  const name = profile ? [profile.first_name, profile.last_name].filter(Boolean).join(" ") || "Coach" : (openUser.name || "Coach");
  // signup_at comes from auth.users (joined in the admin RPC). profiles
  // doesn't carry created_at on this table.
  const signupAgo = profile?.signup_at ? relTime(profile.signup_at) : null;
  const lastActivity = profile?.updated_at ? relTime(profile.updated_at) : null;
  const locationStr = [profile?.city, profile?.country].filter(Boolean).join(", ");

  return (
    <>
      <div onClick={close} style={{position:"fixed",inset:0,zIndex:5500,background:"rgba(0,0,0,0.5)",backdropFilter:"blur(2px)"}}/>
      <div style={{position:"fixed",top:0,right:0,bottom:0,width:380,maxWidth:"90vw",zIndex:5501,
        background:C.cardGradient,borderLeft:`1px solid ${C.cardBorder}`,
        boxShadow:dark?"-10px 0 40px rgba(0,0,0,0.6)":"-6px 0 24px rgba(0,0,0,0.12)",
        display:"flex",flexDirection:"column",overflow:"hidden"}}>
        {/* Header */}
        <div style={{padding:"16px 20px",borderBottom:`1px solid ${C.border}`,display:"flex",alignItems:"center",gap:10}}>
          <Shield size={14} color={GOLD}/>
          <div style={{flex:1,fontSize:11,fontWeight:800,color:GOLD,letterSpacing:1.4,textTransform:"uppercase"}}>Admin · User</div>
          <button onClick={close} aria-label="Close" style={{background:"transparent",border:"none",cursor:"pointer",padding:4,display:"flex",alignItems:"center"}}>
            <X size={16} color={C.textMid}/>
          </button>
        </div>

        {loading ? (
          <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",color:C.textDim,fontSize:13}}>
            Loading…
          </div>
        ) : !profile ? (
          <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",color:C.textDim,fontSize:13,padding:"24px"}}>
            Couldn't load this user (RLS may have blocked it).
          </div>
        ) : (
          <div style={{flex:1,overflow:"auto",padding:"18px 20px"}}>
            {/* Top card */}
            <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:16}}>
              {profile.avatar_url ? (
                <img src={profile.avatar_url} alt={name} style={{width:52,height:52,borderRadius:"50%",objectFit:"cover",border:`1px solid ${C.cardBorder}`}}/>
              ) : (
                <div style={{width:52,height:52,borderRadius:"50%",background:`${GOLD}18`,border:`1px solid ${GOLD}55`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,fontWeight:800,color:GOLD}}>
                  {(name[0]||"C").toUpperCase()}
                </div>
              )}
              <div style={{flex:1,minWidth:0}}>
                {/* Name opens the coach's public ProfilePanel (their posts,
                    bio, location) — the admin panel used to be a one-way
                    street with no path back to what the coach actually posts.
                    Bridged via event since ProfilePanel state lives in
                    BAMFull, not the admin context. */}
                <div className="btn" title="View community profile"
                  onClick={()=>{ try { window.dispatchEvent(new CustomEvent("bam-open-profile", { detail: { name } })); } catch {} }}
                  style={{fontSize:16,fontWeight:800,color:C.text,lineHeight:1.2,cursor:"pointer",display:"inline-block"}}>{name}</div>
                <div style={{fontSize:12,color:C.textDim,marginTop:2}}>{profile.handle ? `@${profile.handle}` : ""}</div>
                {/* The sign-in address. Selectable, because reading it out to
                    someone is half of what this panel gets used for, and
                    editable, because a typo here is the whole bug. */}
                {emailEdit === null ? (
                  profile.email && (
                    <div style={{marginTop:4,display:"flex",alignItems:"center",gap:5,maxWidth:"100%"}}>
                      <Mail size={10} color={C.textDim} style={{flexShrink:0}}/>
                      <span onClick={copyEmail} title="Copy email"
                        style={{fontSize:11.5,color:C.textMid,userSelect:"text",cursor:"pointer",
                          overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                        {profile.email}
                      </span>
                      {emailCopied
                        ? <Check size={10} color={GOLD} style={{flexShrink:0}}/>
                        : <Copy size={10} color={C.textDim} style={{flexShrink:0,opacity:.6}}/>}
                      <button onClick={()=>{setEmailEdit(profile.email||"");setEmailErr(null);}}
                        title="Change sign-in email" aria-label="Change sign-in email"
                        style={{background:"transparent",border:"none",padding:2,display:"flex",
                          cursor:"pointer",flexShrink:0}}>
                        <Pen size={10} color={C.textDim}/>
                      </button>
                    </div>
                  )
                ) : (
                  <div style={{marginTop:5}}>
                    <input value={emailEdit} autoFocus type="email"
                      onChange={e=>setEmailEdit(e.target.value)}
                      onKeyDown={e=>{ if(e.key==="Enter") saveEmail(); if(e.key==="Escape") setEmailEdit(null); }}
                      style={{width:"100%",padding:"6px 8px",borderRadius:7,
                        border:`1px solid ${C.border}`,background:dark?"#0E0E0E":"#FAFAFA",
                        color:C.text,fontSize:12,outline:"none",boxSizing:"border-box",
                        fontFamily:"'DM Sans',sans-serif"}}/>
                    <div style={{display:"flex",gap:6,marginTop:5}}>
                      <button onClick={()=>setEmailEdit(null)}
                        style={{padding:"4px 9px",borderRadius:6,fontSize:11,fontWeight:700,
                          cursor:"pointer",border:`1px solid ${C.border}`,background:"transparent",
                          color:C.textMid}}>Cancel</button>
                      <button onClick={saveEmail} disabled={emailSaving}
                        style={{padding:"4px 9px",borderRadius:6,fontSize:11,fontWeight:800,
                          cursor:emailSaving?"default":"pointer",border:"none",background:GOLD,
                          color:"#111",opacity:emailSaving?.6:1}}>
                        {emailSaving ? "Saving…" : "Save email"}
                      </button>
                    </div>
                    {emailErr && (
                      <div style={{marginTop:5,fontSize:11,color:"#E07070",lineHeight:1.45}}>{emailErr}</div>
                    )}
                  </div>
                )}
              </div>
              {profile.is_admin && (
                <div title="Admin" style={{fontSize:10,fontWeight:800,color:"#111",background:GOLD,padding:"3px 8px",borderRadius:10,letterSpacing:.6}}>ADMIN</div>
              )}
              {profile.is_bam_coach && (
                <div title="BAM Coaches staff" style={{fontSize:10,fontWeight:800,color:"#111",background:GOLD,padding:"3px 8px",borderRadius:10,letterSpacing:.6,display:"inline-flex",alignItems:"center",gap:4}}>
                  <Star size={9} fill="#111" color="#111"/> BAM
                </div>
              )}
            </div>

            {/* Subscription status */}
            <StatusRow C={C} label="Subscription"
              value={profile.stripe_subscription_active && profile.iap_subscription_active ? "Web + iPhone"
                : profile.stripe_subscription_active ? "Active (web)"
                : profile.iap_subscription_active ? "Active (iPhone)" : "Inactive"}
              tone={profile.stripe_subscription_active || profile.iap_subscription_active ? "good" : "warn"}/>
            {profile.exclude_from_metrics && <StatusRow C={C} label="Counts" value={`Test account${profile.exclude_reason ? ` · ${profile.exclude_reason}` : ""}`}/>}
            {profile.role && <StatusRow C={C} label="Role" value={profile.role}/>}
            {signupAgo && <StatusRow C={C} label="Joined" value={signupAgo} icon={<Calendar size={11} color={C.textDim}/>}/>}
            {lastActivity && <StatusRow C={C} label="Last active" value={lastActivity}/>}
            {locationStr && <StatusRow C={C} label="Location" value={locationStr} icon={<MapPin size={11} color={C.textDim}/>}/>}
            {profile.org && <StatusRow C={C} label="Org" value={profile.org}/>}
            {profile.bio && (
              <div style={{marginTop:10,padding:"10px 12px",background:dark?"rgba(255,255,255,0.02)":"rgba(0,0,0,0.02)",borderRadius:10,border:`1px solid ${C.border}`,fontSize:12.5,color:C.textMid,lineHeight:1.55}}>
                {profile.bio}
              </div>
            )}

            {/* Admin notes */}
            <div style={{marginTop:20,paddingTop:16,borderTop:`1px solid ${C.border}`}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
                <div style={{width:22,height:22,borderRadius:6,background:`${GOLD}14`,display:"flex",alignItems:"center",justifyContent:"center"}}><Pen size={11} color={GOLD}/></div>
                <span style={{fontSize:11,fontWeight:800,color:C.text,textTransform:"uppercase",letterSpacing:.8}}>Admin Notes</span>
                <span style={{fontSize:10,color:C.textDim}}>Only admins can see this</span>
              </div>
              <textarea
                value={note}
                onChange={(e)=>{setNote(e.target.value); setSavedAt(null);}}
 placeholder="Sticky notes about this user: billing quirks, program context, support history…"
                rows={5}
                style={{width:"100%",padding:"10px 12px",borderRadius:10,border:`1px solid ${C.border}`,background:dark?"#0E0E0E":"#FAFAFA",color:C.text,fontSize:13,lineHeight:1.55,fontFamily:"'DM Sans',sans-serif",outline:"none",resize:"vertical",boxSizing:"border-box"}}
              />
              <div style={{display:"flex",gap:8,marginTop:10,alignItems:"center"}}>
                <button onClick={save} disabled={saving || note === (profile.admin_notes||"")}
                  style={{padding:"8px 16px",borderRadius:8,fontSize:12.5,fontWeight:800,cursor:(saving||note===(profile.admin_notes||""))?"default":"pointer",
                    border:"none",background:GOLD,color:"#111",opacity:(saving||note===(profile.admin_notes||""))?.55:1}}>
                  {saving ? "Saving…" : "Save note"}
                </button>
                {savedAt && (
                  <span style={{fontSize:11,color:C.textDim,display:"flex",alignItems:"center",gap:5}}>
                    <Check size={11} color={GOLD}/> Saved
                  </span>
                )}
              </div>
            </div>

            {/* Password reset. Above the DM block on purpose: a coach who
                cannot sign in is the one you are most often looking at. */}
            <div style={{marginTop:20,paddingTop:16,borderTop:`1px solid ${C.border}`}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
                <div style={{width:22,height:22,borderRadius:6,background:`${GOLD}14`,display:"flex",alignItems:"center",justifyContent:"center"}}>
                  <KeyRound size={11} color={GOLD}/>
                </div>
                <span style={{fontSize:11,fontWeight:800,color:C.text,textTransform:"uppercase",letterSpacing:.8}}>Password</span>
              </div>
              {/* Which address this resets, said BEFORE the click. A working
                  link for an account the coach is not typing into looks
                  exactly like a broken link from their side, and that is the
                  most common version of "the reset isn't working". */}
              <div style={{fontSize:11.5,color:C.textDim,lineHeight:1.5,marginBottom:8}}>
                Resets the password for <strong style={{color:C.text}}>{profile.email}</strong>. They have to sign in with that exact address.
              </div>
              {!resetLink ? (
                <button onClick={makeResetLink} disabled={resetBusy}
                  style={{width:"100%",padding:"9px 12px",borderRadius:8,fontSize:12.5,fontWeight:700,
                    cursor:resetBusy?"default":"pointer",
                    border:`1px solid ${C.border}`,background:"transparent",color:C.textMid,
                    display:"flex",alignItems:"center",justifyContent:"center",gap:6,opacity:resetBusy?.6:1}}>
                  {resetBusy ? "Generating…" : "Generate reset link"}
                </button>
              ) : (
                <div>
                  <div style={{fontSize:11.5,color:C.textDim,lineHeight:1.5,marginBottom:7}}>
                    Send it anywhere, including Instagram or Slack. It opens a page on our site and only spends the reset when they press the button, so a chat app's link preview cannot burn it.
                  </div>
                  <div style={{padding:"8px 10px",borderRadius:7,border:`1px solid ${C.border}`,
                    background:dark?"#0E0E0E":"#FAFAFA",color:C.textMid,fontSize:11,
                    wordBreak:"break-all",lineHeight:1.45,marginBottom:7,maxHeight:84,overflowY:"auto",
                    fontFamily:"monospace"}}>
                    {resetLink}
                  </div>
                  <div style={{display:"flex",gap:8}}>
                    <button onClick={()=>{setResetLink(null);setResetCopied(false);}}
                      style={{padding:"7px 12px",borderRadius:7,fontSize:12,fontWeight:700,cursor:"pointer",
                        border:`1px solid ${C.border}`,background:"transparent",color:C.textMid}}>Done</button>
                    <button onClick={copyResetLink}
                      style={{flex:1,padding:"7px 12px",borderRadius:7,fontSize:12,fontWeight:800,cursor:"pointer",
                        border:"none",background:GOLD,color:"#111",
                        display:"inline-flex",alignItems:"center",justifyContent:"center",gap:6}}>
                      {resetCopied ? <><Check size={12}/> Copied</> : <><Copy size={12}/> Copy link</>}
                    </button>
                  </div>
                </div>
              )}
              {resetErr && (
                <div style={{marginTop:7,fontSize:11.5,color:"#E07070",lineHeight:1.5}}>{resetErr}</div>
              )}
            </div>

            {/* Admin DM */}
            <div style={{marginTop:20,paddingTop:16,borderTop:`1px solid ${C.border}`}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
                <div style={{width:22,height:22,borderRadius:6,background:`${GOLD}14`,display:"flex",alignItems:"center",justifyContent:"center"}}>
                  <Send size={11} color={GOLD}/>
                </div>
                <span style={{fontSize:11,fontWeight:800,color:C.text,textTransform:"uppercase",letterSpacing:.8}}>Message User</span>
              </div>
              {!dmOpen ? (
                <button onClick={()=>setDmOpen(true)}
                  style={{width:"100%",padding:"9px 12px",borderRadius:8,fontSize:12.5,fontWeight:700,cursor:"pointer",
                    border:`1px solid ${C.border}`,background:"transparent",color:C.textMid,
                    display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
                  Compose email to {profile.first_name || "coach"}
                </button>
              ) : (
                <div>
                  <input value={dmSubject} onChange={e=>setDmSubject(e.target.value)} placeholder="Subject"
                    style={{width:"100%",padding:"8px 10px",borderRadius:7,border:`1px solid ${C.border}`,background:dark?"#0E0E0E":"#FAFAFA",color:C.text,fontSize:12.5,outline:"none",fontFamily:"'DM Sans',sans-serif",boxSizing:"border-box",marginBottom:6}}/>
                  <textarea value={dmBody} onChange={e=>setDmBody(e.target.value)} placeholder="Write your message…" rows={4}
                    style={{width:"100%",padding:"8px 10px",borderRadius:7,border:`1px solid ${C.border}`,background:dark?"#0E0E0E":"#FAFAFA",color:C.text,fontSize:12.5,outline:"none",fontFamily:"'DM Sans',sans-serif",boxSizing:"border-box",resize:"vertical",marginBottom:8}}/>
                  <div style={{display:"flex",gap:8}}>
                    <button onClick={()=>{setDmOpen(false);setDmSubject("");setDmBody("");}}
                      style={{padding:"7px 12px",borderRadius:7,fontSize:12,fontWeight:700,cursor:"pointer",border:`1px solid ${C.border}`,background:"transparent",color:C.textMid}}>Cancel</button>
                    <button onClick={sendDm} disabled={dmSending || !dmSubject.trim() || !dmBody.trim()}
                      style={{flex:1,padding:"7px 12px",borderRadius:7,fontSize:12,fontWeight:800,cursor:(dmSending||!dmSubject.trim()||!dmBody.trim())?"default":"pointer",
                        border:"none",background:GOLD,color:"#111",
                        display:"inline-flex",alignItems:"center",justifyContent:"center",gap:6,opacity:(dmSending||!dmSubject.trim()||!dmBody.trim())?.55:1}}>
                      {dmSent ? <><Check size={12}/> Sent</> : dmSending ? "Sending…" : <>Send email <Send size={12}/></>}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* BAM Coach staff toggle — manual flag, never auto-derived.
                Toggling shows / hides the gold "BAM COACH" pill next to
                this person's name across the platform. Suppresses the
                cert badge (staff don't double-up). */}
            <div style={{marginTop:20,paddingTop:16,borderTop:`1px solid ${C.border}`}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
                <div style={{width:22,height:22,borderRadius:6,background:`${GOLD}14`,display:"flex",alignItems:"center",justifyContent:"center"}}>
                  <Star size={11} color={GOLD}/>
                </div>
                <span style={{fontSize:11,fontWeight:800,color:C.text,textTransform:"uppercase",letterSpacing:.8}}>BAM Coaches Staff</span>
              </div>
              <button onClick={toggleBamCoach} disabled={bamCoachSaving}
                style={{width:"100%",padding:"10px 14px",borderRadius:9,fontSize:12.5,fontWeight:700,
                  cursor:bamCoachSaving?"default":"pointer",
                  border:profile.is_bam_coach ? `1px solid ${GOLD}` : `1px solid ${C.border}`,
                  background:profile.is_bam_coach ? `${GOLD}18` : "transparent",
                  color:profile.is_bam_coach ? GOLD : C.textMid,
                  display:"flex",alignItems:"center",justifyContent:"center",gap:8,
                  opacity:bamCoachSaving?.6:1}}>
                {profile.is_bam_coach
                  ? <><Check size={12}/> Marked as BAM Coach (click to remove)</>
                  : <>Mark as BAM Coach</>}
              </button>
              <div style={{fontSize:11,color:C.textDim,marginTop:8,lineHeight:1.5}}>
                {profile.is_bam_coach
                  ? "Shows the gold BAM COACH pill next to this user's name everywhere on the platform."
 : "Internal staff only: adds a gold BAM COACH pill across the platform."}
              </div>
            </div>

            {/* Intern Hub: make an intern, or a program director. */}
            <InternAdminSection C={C} dark={dark} profile={profile} go={(id) => { window.location.hash = `#/${id}`; }}/>

            {/* Certification access — unlock the cert section without a Stripe
                purchase (coach earned it in person / on a prior system). */}
            <div style={{marginTop:20,paddingTop:16,borderTop:`1px solid ${C.border}`}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
                <div style={{width:22,height:22,borderRadius:6,background:`${GOLD}14`,display:"flex",alignItems:"center",justifyContent:"center"}}>
                  <Award size={11} color={GOLD}/>
                </div>
                <span style={{fontSize:11,fontWeight:800,color:C.text,textTransform:"uppercase",letterSpacing:.8}}>Certification Access</span>
              </div>
              <button onClick={toggleCert} disabled={certSaving}
                style={{width:"100%",padding:"10px 14px",borderRadius:9,fontSize:12.5,fontWeight:700,
                  cursor:certSaving?"default":"pointer",
                  border:profile.cert_purchased ? `1px solid ${GOLD}` : `1px solid ${C.border}`,
                  background:profile.cert_purchased ? `${GOLD}18` : "transparent",
                  color:profile.cert_purchased ? GOLD : C.textMid,
                  display:"flex",alignItems:"center",justifyContent:"center",gap:8,
                  opacity:certSaving?.6:1}}>
                {profile.cert_purchased
                  ? <><Check size={12}/> Cert unlocked (click to lock)</>
                  : <>Unlock certification</>}
              </button>
              <div style={{fontSize:11,color:C.textDim,marginTop:8,lineHeight:1.5}}>
                {profile.cert_purchased
                  ? "This coach can access all certification modules. They may need to reopen the app to see it."
 : "Grants the Certification section without a Stripe purchase: for coaches who earned it in person or on a prior system."}
              </div>
              {/* Founding cert — pre-platform (CoachIQ era) cert buyers were
                  promised FULL platform access for life. This flag is what
                  the membership gate + cert-comp-sweep read. */}
              <button onClick={toggleFounding} disabled={foundingSaving}
                style={{width:"100%",marginTop:10,padding:"10px 14px",borderRadius:9,fontSize:12.5,fontWeight:700,
                  cursor:foundingSaving?"default":"pointer",
                  border:profile.cert_founding ? `1px solid ${GOLD}` : `1px solid ${C.border}`,
                  background:profile.cert_founding ? `${GOLD}18` : "transparent",
                  color:profile.cert_founding ? GOLD : C.textMid,
                  display:"flex",alignItems:"center",justifyContent:"center",gap:8,
                  opacity:foundingSaving?.6:1}}>
                {profile.cert_founding
 ? <><Check size={12}/> Founding cert: platform free for life</>
                  : <>Mark as founding cert (pre-platform buyer)</>}
              </button>
              <div style={{fontSize:11,color:C.textDim,marginTop:8,lineHeight:1.5}}>
                {profile.cert_founding
 ? "Bought the cert before the platform existed: full platform access forever, never billed. The comp sweep cancels any stray subscription."
                  : "Only for coaches who bought the cert BEFORE the platform launched. Cert buyers from the platform era keep cert content but still subscribe for the platform."}
              </div>
            </div>

            {/* Test / internal: left out of admin numbers (C3). */}
            <div style={{marginTop:20,paddingTop:16,borderTop:`1px solid ${C.border}`}}>
              <button onClick={toggleTest} disabled={testSaving}
                style={{width:"100%",padding:"10px 14px",borderRadius:9,fontSize:12.5,fontWeight:700,
                  cursor:testSaving?"default":"pointer",
                  border:profile.exclude_from_metrics ? `1px solid ${GOLD}` : `1px solid ${C.border}`,
                  background:profile.exclude_from_metrics ? `${GOLD}18` : "transparent",
                  color:profile.exclude_from_metrics ? GOLD : C.textMid,
                  display:"flex",alignItems:"center",justifyContent:"center",gap:8,
                  opacity:testSaving?.6:1}}>
                {profile.exclude_from_metrics
                  ? <><Check size={12}/> Test or internal account</>
                  : <>Mark as test or internal account</>}
              </button>
              <div style={{fontSize:11,color:C.textDim,marginTop:8,lineHeight:1.5}}>
                {profile.exclude_from_metrics
                  ? "Left out of every admin count: members, signups, subscribers, the at-risk list. Their access doesn't change."
                  : "For your own test logins and staff accounts. Only changes the numbers, never what they can use."}
              </div>
            </div>

            {/* Suspend / reactivate */}
            <div style={{marginTop:20,paddingTop:16,borderTop:`1px solid ${C.border}`}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
                <div style={{width:22,height:22,borderRadius:6,background:"rgba(248,113,113,0.12)",display:"flex",alignItems:"center",justifyContent:"center"}}>
                  <Ban size={11} color="#F87171"/>
                </div>
                <span style={{fontSize:11,fontWeight:800,color:C.text,textTransform:"uppercase",letterSpacing:.8}}>Account Status</span>
              </div>
              <button onClick={toggleSuspend} disabled={suspending}
                style={{width:"100%",padding:"10px 14px",borderRadius:9,fontSize:12.5,fontWeight:700,cursor:suspending?"default":"pointer",
                  border:profile.is_suspended ? `1px solid ${GOLD}55` : "1px solid rgba(248,113,113,0.35)",
                  background:profile.is_suspended ? `${GOLD}14` : "rgba(248,113,113,0.08)",
                  color:profile.is_suspended ? GOLD : "#F87171",
                  display:"flex",alignItems:"center",justifyContent:"center",gap:8,opacity:suspending?.6:1}}>
                {profile.is_suspended ? "Reactivate account" : "Suspend this user"}
              </button>
              {profile.is_suspended && (
                <div style={{fontSize:11,color:C.textDim,marginTop:8,lineHeight:1.5}}>
 User is currently suspended: they can't access paid content.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function StatusRow({ C, label, value, icon, tone }) {
  const badgeBg = tone === "good" ? "rgba(90,181,132,0.14)" : tone === "warn" ? "rgba(248,113,113,0.14)" : "transparent";
  const badgeColor = tone === "good" ? "#5AB584" : tone === "warn" ? "#F87171" : C.textMid;
  return (
    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 0",borderBottom:`1px solid ${C.border}`}}>
      <div style={{display:"flex",alignItems:"center",gap:6,fontSize:11,color:C.textDim,textTransform:"uppercase",letterSpacing:.8,fontWeight:700}}>
        {icon}{label}
      </div>
      <div style={{fontSize:12.5,color:badgeColor,fontWeight:700,background:badgeBg,padding:tone?"3px 9px":"0",borderRadius:10}}>{value}</div>
    </div>
  );
}

function relTime(iso) {
  try {
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    const days = Math.floor(diff / 86400000);
    if (days < 1) return "today";
    if (days === 1) return "yesterday";
    if (days < 30) return `${days}d ago`;
    const months = Math.floor(days / 30);
    if (months < 12) return `${months}mo ago`;
    return `${Math.floor(months/12)}y ago`;
  } catch { return ""; }
}

export default UserAdminPanel;
